import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

/** Status messages that rotate while the overlay is visible */
const DEFAULT_MESSAGES = [
  'Deine Daten werden geprüft …',
  'Einen Moment noch …',
  'Sollte nicht mehr lange dauern …',
  'Fast geschafft …',
];

/** Interval (ms) between cycling to the next status message */
const MESSAGE_INTERVAL = 5000;

interface SubmissionLoadingOverlayProps {
  /** Whether the overlay is visible */
  open: boolean;
  /** Custom messages to cycle through (optional, uses defaults otherwise) */
  messages?: string[];
}

/**
 * Full-viewport loading overlay with a spinner and rotating status texts.
 *
 * Uses MUI `Backdrop` (position: fixed) so it stays visible regardless of
 * scroll position — important for long forms where the user has scrolled
 * past the center of the page before submitting.
 */
export const SubmissionLoadingOverlay = ({
  open,
  messages = DEFAULT_MESSAGES,
}: SubmissionLoadingOverlayProps) => {
  const [messageIndex, setMessageIndex] = useState(0);

  // Reset to first message when the overlay opens, then cycle through messages
  useEffect(() => {
    if (!open) {
      setMessageIndex(0);
      return;
    }

    const timer = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, MESSAGE_INTERVAL);

    return () => clearInterval(timer);
  }, [open, messages.length]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: 1300,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        // Stack contents vertically and center them in the viewport
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <CircularProgress size={56} sx={{ color: 'primary.main' }} />
      <Box
        sx={{
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'common.white',
            fontWeight: 500,
            textAlign: 'center',
            // Fade-in on each message change via CSS animation
            animation: 'fadeIn 0.4s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
          key={messageIndex}
        >
          {messages[messageIndex]}
        </Typography>
      </Box>
    </Backdrop>
  );
};
